import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const reviewSteps = [
  {
    step: '01',
    icon: 'ri-search-eye-line',
    title: 'Pre-Screen & Eligibility',
    description:
      'We verify licenses, accepted countries, basic security, and complaint history before a casino enters our testing pipeline.',
  },
  {
    step: '02',
    icon: 'ri-flask-line',
    title: 'Hands-On Testing',
    description:
      'Our reviewers create real accounts, test payments, contact support, and play across desktop and mobile to assess day-to-day user experience.',
  },
  {
    step: '03',
    icon: 'ri-scales-3-line',
    title: 'Scoring & Weighting',
    description:
      'Each category has a weighted score. Safety and fairness carry the highest impact to keep ratings player-first and transparent.',
  },
  {
    step: '04',
    icon: 'ri-refresh-line',
    title: 'Continuous Re-Checks',
    description:
      'Casinos are re-evaluated regularly. If terms, payout speed, or trust signals change, we update rankings and highlight changes.',
  },
];

const scoreWeights = [
  { label: 'Licensing & Security', percent: 89, color: 'bg-emerald-500' },
  { label: 'Payments & Payout Speed', percent: 80, color: 'bg-blue-500' },
  { label: 'Games & Software Quality', percent: 95, color: 'bg-violet-500' },
  { label: 'Bonuses & Fair Terms', percent: 85, color: 'bg-amber-500' },
  { label: 'Customer Support', percent: 75, color: 'bg-pink-500' },
  { label: 'Mobile Experience', percent: 83, color: 'bg-cyan-500' },
];

const standards = [
  {
    icon: 'ri-shield-star-line',
    title: 'Trust First',
    description: 'No recommendation without valid licensing, encryption, and responsible gambling safeguards.',
  },
  {
    icon: 'ri-eye-line',
    title: 'Transparent Ratings',
    description: 'We show what we test and why it matters, so players can compare casinos with confidence.',
  },
  {
    icon: 'ri-user-heart-line',
    title: 'Player-Centered',
    description: 'Real user pain points guide our scoring: withdrawals, support quality, and terms clarity.',
  },
  {
    icon: 'ri-time-line',
    title: 'Always Current',
    description: 'Rankings are reviewed continuously to reflect policy changes, promotions, and product updates.',
  },
];

export default function MethodologyPage() {
  return (
    <>
      <Navbar />
      <main className="bg-slate-50 min-h-screen">
        <section className="bg-gradient-to-br from-emerald-600 to-green-700 text-white">
          <div className="max-w-7xl mx-auto px-6 py-16">
            <p className="text-emerald-100 font-semibold mb-3 tracking-wide uppercase text-sm">
              KoalaBet Methodology
            </p>
            <h1 className="text-3xl md:text-5xl font-bold mb-5 leading-tight">
              How We Review Online Casinos
            </h1>
            <p className="max-w-3xl text-emerald-50 text-base md:text-lg leading-relaxed">
              Every KoalaBet rating comes from real testing, not marketing claims. Our team follows a
              structured framework designed to measure safety, value, and player experience with clear,
              repeatable standards.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <span className="px-4 py-2 rounded-full bg-white/15 border border-white/20 text-sm font-medium">
                200+ checkpoints per review
              </span>
              <span className="px-4 py-2 rounded-full bg-white/15 border border-white/20 text-sm font-medium">
                Real account testing
              </span>
              <span className="px-4 py-2 rounded-full bg-white/15 border border-white/20 text-sm font-medium">
                Monthly data refresh
              </span>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 py-14">
          <div className="grid lg:grid-cols-2 gap-8">
            <article className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What Makes Our Reviews Different</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We focus on the details that directly affect real players: how fast money arrives, how
                fair bonus terms are, whether support actually solves problems, and if security standards
                are consistently maintained.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Each review is written by specialists and cross-checked by editors before publication.
                This two-layer process helps remove bias and keeps scores aligned with our published
                criteria.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We also track post-publication changes. If a casino lowers quality, adds hidden terms, or
                slows payouts, its score can drop quickly to reflect the new reality.
              </p>
            </article>

            <aside className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Score Weighting</h2>
              <div className="space-y-4">
                {scoreWeights.map((item) => (
                  <div key={item.label}>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="font-semibold text-gray-800">{item.label}</span>
                      <span className="text-gray-600">{item.percent}%</span>
                    </div>
                    <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`${item.color} h-full rounded-full`} style={{ width: `${item.percent}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </aside>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 pb-14">
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Our 4-Step Review Flow</h2>
            <p className="text-gray-600 max-w-3xl">
              From first screening to ongoing monitoring, this process keeps every rating consistent,
              explainable, and up to date.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {reviewSteps.map((item) => (
              <div
                key={item.step}
                className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-emerald-700 tracking-wide mb-1">STEP {item.step}</p>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-sm">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 pb-16">
          <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">KoalaBet Review Principles</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {standards.map((item) => (
                <div key={item.title} className="rounded-xl border border-gray-200 p-5 bg-gray-50">
                  <div className="w-11 h-11 rounded-lg bg-green-100 flex items-center justify-center mb-3">
                    <i className={`${item.icon} text-xl text-green-700`}></i>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-xl bg-emerald-50 border border-emerald-200 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Want to compare casinos now?</h3>
                <p className="text-sm text-gray-700">
                  Explore our latest rankings built with this methodology.
                </p>
              </div>
              <Link
                to="/"
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-colors"
              >
                Back to Rankings
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
